import React, { useEffect, useRef, useState } from 'react';
import {
  Box, CircularProgress, Stack, Typography,
} from '@mui/material';
import api from '../../utils/functions/api';

const apiBase = process.env.REACT_APP_API_URL;
const MAX_PAGES = 30;

// โหลด pdf.js ครั้งเดียวตอนมี PDF ให้แสดงจริง (แยก chunk ไม่ถ่วง bundle หลัก)
// ใช้ legacy build รุ่น 2.16 เพราะ react-scripts 4 (webpack 4) parse build ใหม่ๆ ไม่ได้ และรัน worker ใน main thread
// (ตั้ง window.pdfjsWorker ให้ pdf.js ใช้ "fake worker") เพื่อไม่ต้องตั้งค่า worker file แยกใน CRA
let pdfjsPromise = null;
const loadPdfjs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = Promise.all([
      import('pdfjs-dist/legacy/build/pdf'),
      import('pdfjs-dist/legacy/build/pdf.worker'),
    ]).then(([pdfjs, worker]) => {
      window.pdfjsWorker = worker;
      return pdfjs;
    }).catch((err) => {
      pdfjsPromise = null;
      throw err;
    });
  }
  return pdfjsPromise;
};

// แสดง PDF ด้วย pdf.js วาดลง canvas เอง — หน้าตาเหมือนกันทุก OS/เบราว์เซอร์ (ตัวดู PDF ในตัวของเบราว์เซอร์
// แสดงใน iframe ไม่ได้บน Android Chrome และ iOS บางรุ่นเห็นแค่หน้าแรก) ไฟล์โหลดผ่าน backend
// (/announcement/:id/attachment) เพราะ pdf.js ดึงจาก GCS ตรงๆ ไม่ได้ (CORS)
export default function PdfViewer({ announcementId, url, name }) {
  const pagesRef = useRef(null);
  const [status, setStatus] = useState({ loading: true, error: false, pages: 0 });

  useEffect(() => {
    let cancelled = false;
    let pdfDoc = null;
    setStatus({ loading: true, error: false, pages: 0 });

    (async () => {
      try {
        const [pdfjs, { data }] = await Promise.all([
          loadPdfjs(),
          api.get(`${apiBase}/announcement/${announcementId}/attachment`, {
            params: { url },
            responseType: 'arraybuffer',
          }),
        ]);
        pdfDoc = await pdfjs.getDocument({ data }).promise;
        const container = pagesRef.current;
        if (cancelled || !container) return;

        container.innerHTML = '';
        const width = container.clientWidth || 600;
        const ratio = window.devicePixelRatio || 1;
        const total = Math.min(pdfDoc.numPages, MAX_PAGES);

        // วาดทีละหน้าตามลำดับ (ตั้งใจ await ใน loop — หน้าแรกขึ้นก่อน ไม่ต้องรอทั้งไฟล์)
        for (let i = 1; i <= total; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const page = await pdfDoc.getPage(i);
          if (cancelled) return;
          const viewport = page.getViewport({ scale: (width / page.getViewport({ scale: 1 }).width) * ratio });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = '100%';
          canvas.style.display = 'block';
          canvas.style.background = '#fff';
          canvas.style.boxShadow = '0 1px 4px rgba(0,0,0,0.15)';
          canvas.style.marginBottom = '10px';
          container.appendChild(canvas);
          // eslint-disable-next-line no-await-in-loop
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          if (i === 1) setStatus({ loading: false, error: false, pages: pdfDoc.numPages });
        }
      } catch (err) {
        if (!cancelled) setStatus({ loading: false, error: true, pages: 0 });
      }
    })();

    return () => {
      cancelled = true;
      if (pdfDoc) pdfDoc.destroy();
    };
  }, [announcementId, url]);

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          maxHeight: { xs: '65vh', md: '72vh' },
          overflowY: 'auto',
          p: 1.25,
          borderRadius: 2,
          bgcolor: '#e5e7eb',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {status.loading && (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="caption" color="text.secondary">กำลังโหลด PDF…</Typography>
          </Stack>
        )}
        {status.error && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            แสดง PDF ไม่ได้ — กดลิงก์ด้านล่างเพื่อเปิดไฟล์
          </Typography>
        )}
        <Box ref={pagesRef} />
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography
          variant="caption"
          component="a"
          href={url}
          target="_blank"
          rel="noreferrer"
          sx={{ color: 'primary.main' }}
        >
          {name || 'PDF'} — เปิดในหน้าต่างใหม่
        </Typography>
        {status.pages > 0 && (
          <Typography variant="caption" color="text.secondary">
            {status.pages > MAX_PAGES ? `แสดง ${MAX_PAGES} จาก ${status.pages} หน้า` : `${status.pages} หน้า`}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
